import { intersection } from 'lodash-es';
import { z } from 'zod';
import { GroupResponse } from '~/connect/types';
import { EMAIL_MESSAGE, REQUIRED_MESSAGE, requiredString } from '@wyz/utils';
import { GROUPS } from '~/common/helpers';

export const USER_STATUSES = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
} as const;

export const userFormSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  internalCode: z.string().optional(),
  entities: z.array(
    z.object({
      value: z.string(),
      label: z.string(),
      level: z.number().optional(),
    }),
  ),
  email: z
    .email({
      error: (issue) => (!issue.input ? REQUIRED_MESSAGE : EMAIL_MESSAGE),
    })
    .min(1, {
      message: REQUIRED_MESSAGE,
    }),
  groups: z.array(z.string()).min(1, {
    message: REQUIRED_MESSAGE,
  }),
  roles: z.array(z.string()).nullish(),
  locale: requiredString,
  supplierHqId: z.number().nullish(),
});

export const getUserFormSchema = (wyzAccount: boolean, groups: GroupResponse) =>
  userFormSchema.superRefine((user, ctx) => {
    const requiresEntityBinding =
      !wyzAccount &&
      intersection(
        groups.authorizedGroups.map((g) => g.codename),
        user.groups,
      ).length === 0;

    if (requiresEntityBinding && user.entities.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: REQUIRED_MESSAGE,
        path: ['entities'],
        fatal: true,
      });
    }

    if (
      user.groups.includes(GROUPS.GROUP_CONTAINER_SUPPLIER) &&
      !user.supplierHqId
    ) {
      ctx.addIssue({
        code: 'custom',
        message: REQUIRED_MESSAGE,
        path: ['supplierHqId'],
        fatal: true,
      });
    }
  });

export const userSchema = userFormSchema.extend({
  id: z.string(),
  locale: z.string(),
  status: z.enum(USER_STATUSES),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  lastLoginAt: z.date().nullish(),
  entities: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      code: z.string(),
      deliveryInformation: z.object({ city: z.string() }),
      level: z.number(),
    }),
  ),
});
export const userPayloadSchema = userSchema
  .omit({ id: true, entities: true, status: true, supplierHq: true })
  .extend({
    entities: z.array(z.object({ id: z.string() })),
    supplierHqId: z.number().nullish(),
  });
